import { type ReactNode, useState } from "react";
import { Panel } from "@/components/ui/Panel";
import { X } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FigureDrawing } from "@/v2/figures/FigureDrawing";
import { named, type ShownFigure } from "@/lib/figure-naming";

/**
 * One figure's drawing, opened above the arrangement at the size it was drawn.
 *
 * The card shows it at 46 by 38 with `object-cover`, which crops a wide drawing
 * to its middle. Read-only: per-figure editing lives on the Define stage.
 */
export function FigurePreview({
  figure,
  drawingUrl,
  children,
}: {
  figure: ShownFigure;
  /**
   * The image to open, where the caller already holds one. The summaries show
   * the rendered formal drawing rather than the stored thumbnail, and opening
   * a different picture from the one that was clicked would be its own bug.
   */
  drawingUrl?: string;
  /** The card's own thumbnail, which becomes the control that opens this. */
  children: ReactNode;
}) {
  const [failed, setFailed] = useState(false);

  return (
    // The failure is forgotten on the way in, because the message tells the
    // attorney to try again and a remembered one would answer that with itself.
    <Dialog
      onOpenChange={(opening) => {
        if (opening) setFailed(false);
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={`Preview ${named(figure)}`}
          title="Preview figure"
          data-testid={`figure-preview-${figure.file_id}`}
          className="flex-none cursor-zoom-in rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          {children}
        </button>
      </DialogTrigger>

      <DialogContent
        // The arrangement behind listens for Escape too, to leave the mode that
        // asks where a new group goes, so one key press would otherwise close
        // this and cancel that. Not `preventDefault`: the dismissable layer
        // closes only on an event whose default still stands.
        onEscapeKeyDown={(event) => {
          event.stopPropagation();
        }}
        className="max-w-[min(800px,96vw)] grid-rows-[auto_minmax(0,1fr)] gap-3 p-4"
      >
        <DialogTitle className="flex min-w-0 items-center gap-2 pr-8 text-sm">
          <span className="font-mono text-xs font-bold">
            {figure.label === null ? "Not placed" : `FIG. ${figure.label}`}
          </span>
          <span className="truncate font-mono text-[11.5px] font-normal text-muted-foreground">
            {figure.original_filename}
          </span>
        </DialogTitle>

        <DialogClose className="absolute top-3 right-3 rounded-sm p-1 text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:outline-none">
          <X className="size-4" />
          <span className="sr-only">Close</span>
        </DialogClose>

        {/* `min-h-0`: a grid item defaults to `min-height: auto`, so a drawing
            taller than the screen would push the dialog past the viewport
            instead of scrolling inside it. */}
        <div className="min-h-0 overflow-auto">
          {failed ? (
            <Panel
              as="p"
              shape="row"
              data-testid="figure-preview-failed"
              className="px-4 py-10 text-center text-sm text-muted-foreground"
            >
              This drawing could not be loaded. Reload the page and try again.
            </Panel>
          ) : (
            <FigureDrawing
              thumbnailUrl={drawingUrl ?? figure.thumbnail_url}
              filename={figure.original_filename}
              testId="figure-preview-drawing"
              onError={() => {
                setFailed(true);
              }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
